import React, { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, BookOpen, UserPlus, Download, MapPin, HandCoins } from "lucide-react";
import { LogoMark } from "./Logo";
import { Link } from "react-router-dom";
import { usePublicRed } from "./red/api";
import { trackBookDownload } from "../lib/trackBookDownload";

const LINKS = [
  { id: "propuesta-estrella", label: "Casa a los 18" },
  { id: "doctrina", label: "Doctrina" },
  { id: "simulador", label: "Capital Ciudadano" },
  { id: "politicas", label: "10 Políticas" },
  { id: "sector-por-sector", label: "Sector por Sector" },
  { id: "libro", label: "Libro" },
  { id: "multimedia", label: "Multimedia" },
];

const BOOK_PDF =
  "https://customer-assets.emergentagent.com/job_ya-digital/artifacts/riplp2t7_Resumen_por_capitulos_Capitalismo_de_Abundancia.pdf";

const SUMATE_ITEMS = [
  { id: "doctrina", label: "Conocé la propuesta", icon: BookOpen },
  { id: "sumate", label: "Sumate como voluntario", icon: UserPlus },
  { id: "manifiesto", label: "Descargá el manifiesto", icon: Download, href: BOOK_PDF, external: true },
  { id: "organizar", label: "Organizá en tu ciudad", icon: MapPin },
  { id: "aportar", label: "Aportá a la campaña", icon: HandCoins },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [sumateOpen, setSumateOpen] = useState(false);
  const sumateRef = useRef(null);
  const red = usePublicRed();
  const redLinks = [
    ...(red.activa ? [{ to: "/biblioteca", label: "Biblioteca", id: "biblioteca" }] : []),
    ...(red.gira ? [{ to: "/gira", label: "Gira", id: "gira" }] : []),
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (sumateRef.current && !sumateRef.current.contains(e.target)) {
        setSumateOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const go = (id) => {
    setOpen(false);
    setSumateOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? "border-b border-gray-200 shadow-sm" : ""
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <button onClick={() => go("inicio")} className="flex items-center" data-testid="nav-home">
          <LogoMark />
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-[#0A192F] hover:text-[#0A2463] transition-colors tracking-wide font-headings uppercase"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          {redLinks.map((l) => (
            <Link key={l.id} to={l.to} className="hidden xl:inline text-sm font-medium text-[#0A192F] hover:text-[#0A2463] transition-colors tracking-wide font-headings uppercase" data-testid={`nav-link-${l.id}`}>
              {l.label}
            </Link>
          ))}

          {/* Sumate dropdown */}
          <div ref={sumateRef} className="relative">
            <button
              onClick={() => setSumateOpen((s) => !s)}
              className="bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] font-headings uppercase tracking-wide text-sm px-5 py-2.5 transition-colors inline-flex items-center gap-2"
              data-testid="nav-cta-sumate"
              aria-expanded={sumateOpen}
              aria-haspopup="menu"
            >
              Sumate
              <ChevronDown
                size={16}
                className={`transition-transform ${sumateOpen ? "rotate-180" : ""}`}
              />
            </button>

            {sumateOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 shadow-xl py-2 z-50"
                data-testid="nav-sumate-dropdown"
              >
                {SUMATE_ITEMS.map((item) => {
                  const Icon = item.icon;
                  if (item.external) {
                    return (
                      <a
                        key={item.id}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          setSumateOpen(false);
                          if (item.id === "manifiesto") trackBookDownload("navbar");
                        }}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-[#0A192F] hover:bg-[#F5B400]/10 hover:text-[#0A2463] font-headings uppercase tracking-wide transition-colors"
                        data-testid={`nav-sumate-${item.id}`}
                      >
                        <Icon size={16} className="text-[#F5B400]" />
                        {item.label}
                      </a>
                    );
                  }
                  return (
                    <button
                      key={item.id}
                      onClick={() => go(item.id)}
                      className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-[#0A192F] hover:bg-[#F5B400]/10 hover:text-[#0A2463] font-headings uppercase tracking-wide transition-colors"
                      data-testid={`nav-sumate-${item.id}`}
                    >
                      <Icon size={16} className="text-[#F5B400]" />
                      {item.label}
                    </button>
                  );
                })}
                {redLinks.map((l) => (
                  <Link key={l.id} to={l.to} onClick={() => setSumateOpen(false)} className="xl:hidden flex items-center gap-3 px-4 py-3 text-sm text-[#0A192F] hover:bg-[#F5B400]/10 hover:text-[#0A2463] font-headings uppercase tracking-wide transition-colors border-t border-gray-100" data-testid={`nav-sumate-${l.id}`}>
                    <BookOpen size={16} className="text-[#F5B400]" />
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <button
          className="lg:hidden text-[#0A192F] p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="text-left py-3 text-base font-medium font-headings uppercase tracking-wide text-[#0A192F] border-b border-gray-100"
                data-testid={`nav-mobile-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            {redLinks.map((l) => (
              <Link key={l.id} to={l.to} onClick={() => setOpen(false)} className="text-left py-3 text-base font-medium font-headings uppercase tracking-wide text-[#0A192F] border-b border-gray-100" data-testid={`nav-mobile-link-${l.id}`}>
                {l.label}
              </Link>
            ))}

            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5B400] font-headings mb-2 px-1">
                Sumate
              </div>
              {SUMATE_ITEMS.map((item) => {
                const Icon = item.icon;
                if (item.external) {
                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        setOpen(false);
                        if (item.id === "manifiesto") trackBookDownload("navbar");
                      }}
                      className="flex items-center gap-3 py-3 text-sm text-[#0A192F] font-headings uppercase tracking-wide"
                      data-testid={`nav-mobile-sumate-${item.id}`}
                    >
                      <Icon size={16} className="text-[#F5B400]" />
                      {item.label}
                    </a>
                  );
                }
                return (
                  <button
                    key={item.id}
                    onClick={() => go(item.id)}
                    className="w-full text-left flex items-center gap-3 py-3 text-sm text-[#0A192F] font-headings uppercase tracking-wide"
                    data-testid={`nav-mobile-sumate-${item.id}`}
                  >
                    <Icon size={16} className="text-[#F5B400]" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
